import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import i18n from '../../utils/i18n';
import {screenType} from '../../constants/analytics';
import IssueCreateFormContainer from '../issues/IssueCreateFormContainer';
import Loader from '@adsk/bim360-matrix-react-loader';
import SimpleModal from '@adsk/bim360-matrix-react-simple-modal';
import {TYPE_QUALITY_ISSUES} from '../../constants/issue-types';
import IssueNewModalFooter from './IssueNewModalFooter';
import cx from 'classnames';

const IssueNewModal = ({
  isOpen,
  resetFormAction,
  resetValidityFormAction,
  container,
  onClose,
  onSuccess,
  allowCreateAnother,
  loading,
  additionalAttributes,
  initialFieldValues,
  localStoragePrefix,
  defaultIssueSubtypeId,
  availableStatuses,
  className
}) => {
  const [isCreateAnotherChecked, setIsCreateAnotherChecked] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const issueCreateFormRef = useRef(null);
  const onSaveStart = useCallback(() => {
    setIsSubmitting(true);
  }, []);
  const onSaveSuccess = useCallback(() => {
    setIsSubmitting(false);
  }, []);
  const onSaveError = useCallback(() => {
    setIsSubmitting(false);
  }, []);
  const save = useCallback(() => {
    issueCreateFormRef.current && issueCreateFormRef.current.saveIssue();
  }, []);

  const resetForm = useCallback(() => {
    resetFormAction('pendingIssue');
    resetValidityFormAction('pendingIssue');
  }, [resetFormAction, resetValidityFormAction]);

  useEffect(
    () => () => {
      isOpen && resetForm();
    },
    [isOpen, resetForm]
  );

  const handleSaveSuccess = useCallback(successResponse => {
    onSaveSuccess();
    if (!isCreateAnotherChecked) {
      onClose();
    }
    onSuccess && onSuccess(successResponse);
  }, [onSaveSuccess, isCreateAnotherChecked, onClose, onSuccess]);

  return (
    <SimpleModal
      show={isOpen}
      title={i18n.t('projectGrid.newModal.title', {issueType: TYPE_QUALITY_ISSUES})}
      className={cx(className, 'IssueNewModal')}
      container={container}
      onExited={onClose}
      disableModalExit={isSubmitting}
      footer={<IssueNewModalFooter
        isSubmitting={isSubmitting}
        allowCreateAnother={allowCreateAnother}
        isCreateAnotherChecked={isCreateAnotherChecked}
        setIsCreateAnotherChecked={setIsCreateAnotherChecked}
        save={save}
        onClose={onClose}
        container={container}
      />}
      backdrop="static"
    >
      {loading ? (
        <Loader />
      ) : (
        <div className="isui-form">
          <IssueCreateFormContainer
            ref={issueCreateFormRef}
            origin={screenType.PROJECT}
            onSaveStart={onSaveStart}
            onSaveSuccess={handleSaveSuccess}
            onSaveError={onSaveError}
            disabled={isSubmitting}
            container={container}
            additionalAttributes={additionalAttributes}
            initialFieldValues={initialFieldValues}
            localStoragePrefix={localStoragePrefix}
            defaultIssueSubtypeId={defaultIssueSubtypeId}
            availableStatuses={availableStatuses}
            createAnother={isCreateAnotherChecked}
            focusForm
            allowLinkToDocument
          />
        </div>
      )}
    </SimpleModal>
  );
};

IssueNewModal.displayName = 'IssueNewModal';

IssueNewModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  allowCreateAnother: PropTypes.bool,
  container: PropTypes.object,
  resetFormAction: PropTypes.func.isRequired,
  resetValidityFormAction: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  loading: PropTypes.bool,
  additionalAttributes: PropTypes.object,
  initialFieldValues: PropTypes.object,
  localStoragePrefix: PropTypes.string,
  defaultIssueSubtypeId: PropTypes.string,
  availableStatuses: PropTypes.array,
  className: PropTypes.string
};

export default IssueNewModal;
